import React, { useState, useEffect } from 'react';

const BACKEND = 'https://primo.flexicloud.pt/api';

const TEAM = [
  { name: 'David Barroco', gid: '1211829048672481', role: 'lead' },
  { name: 'Nuno Simoes', gid: '1211830509404116', role: 'pm' },
  { name: 'Jairo Ferreira', gid: '1213818365382735', role: 'pm' },
];

function getToken() { return localStorage.getItem('primo_token'); }
function setToken(t) { localStorage.setItem('primo_token', t); }
function clearToken() { localStorage.removeItem('primo_token'); }

function authHeaders() {
  const t = getToken();
  return t ? { 'Authorization': `Bearer ${t}` } : {};
}

async function apiFetch(path, options = {}) {
  const res = await fetch(`${BACKEND}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(options.headers || {}) }
  });
  if (res.status === 401) { clearToken(); window.location.reload(); return null; }
  return res;
}

function formatText(text) {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    if (line.startsWith('# ')) return (
      <h2 key={i} style={{ fontSize: '18px', fontWeight: '500', margin: '0 0 4px' }}>
        {line.replace('# ', '')}
      </h2>
    );
    if (line.startsWith('## ')) {
      const color = line.includes('CRITICAL') || line.includes('MUST') ? '#c0392b'
        : line.includes('HIGH') || line.includes('RISK') || line.includes('SIGNALS') ? '#e67e22'
        : line.includes('OVERALL') || line.includes('KEY') ? '#1a73e8'
        : '#27ae60';
      return (
        <h3 key={i} style={{ fontSize: '13px', fontWeight: '500', color, margin: '20px 0 8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {line.replace('## ', '')}
        </h3>
      );
    }
    if (line.startsWith('**') && line.endsWith('**')) return (
      <p key={i} style={{ fontWeight: '500', margin: '12px 0 4px', fontSize: '13px' }}>
        {line.replace(/\*\*/g, '')}
      </p>
    );
    if (line.startsWith('- ') || line.startsWith('* ')) {
      const text = line.replace(/^[-*]\s/, '').replace(/\*\*/g, '');
      const isOverdue = text.includes('[OVERDUE');
      const isDueToday = text.includes('[DUE TODAY]');
      return (
        <p key={i} style={{
          margin: '4px 0', fontSize: '13px', paddingLeft: '12px',
          color: isOverdue ? '#c0392b' : isDueToday ? '#e67e22' : 'inherit',
          fontWeight: isOverdue || isDueToday ? '500' : 'normal'
        }}>
          {text}
        </p>
      );
    }
    if (line.startsWith('---')) return (
      <hr key={i} style={{ border: 'none', borderTop: '1px solid #eee', margin: '12px 0' }} />
    );
    if (line.trim() === '') return <br key={i} />;
    return <p key={i} style={{ margin: '4px 0', fontSize: '13px' }}>{line.replace(/\*\*/g, '')}</p>;
  });
}

function formatPlan(text) {
  if (!text) return null;
  const DAY_HEADERS = ['Segunda-feira', 'Terca-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira'];
  const OTHER_SECTIONS = ['AGUARDA CLIENTE', 'MONITOR', 'ASANA HOUSEKEEPING', 'NOTES FOR NEXT WEEK'];

  const lines = text.split('\n');
  const sections = [];
  let current = null;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed === '---') continue;
    const isWeekTitle = trimmed.startsWith('#') && !trimmed.startsWith('##');
    const isDayHeader = trimmed.startsWith('##') && DAY_HEADERS.some(d => trimmed.includes(d));
    const isOtherHeader = trimmed.startsWith('##') && OTHER_SECTIONS.some(s => trimmed.toUpperCase().includes(s));

    if (isWeekTitle) {
      sections.push({ type: 'week_title', text: trimmed.replace(/^#+\s*/, '') });
      current = null;
    } else if (isDayHeader) {
      const isHoliday = trimmed.toUpperCase().includes('FERIADO');
      current = { type: 'day', header: trimmed.replace(/^#+\s*/, ''), items: [], holiday: isHoliday };
      sections.push(current);
    } else if (isOtherHeader) {
      current = { type: 'other', header: trimmed.replace(/^#+\s*/, ''), items: [] };
      sections.push(current);
    } else if (current) {
      current.items.push(trimmed);
    }
  }

  const renderItems = (items, small) => items.map((item, j) => {
    const text = item.replace(/^[-•*]\s*/, '').replace(/\*\*/g, '');
    const isSubheader = item.startsWith('**') && item.endsWith('**');
    const isCarryover = text.toLowerCase().includes('carry-over');
    if (isSubheader) return (
      <p key={j} style={{ fontSize: '11px', fontWeight: '600', color: '#888', margin: '10px 0 4px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{text}</p>
    );
    return (
      <p key={j} style={{
        fontSize: small ? '12px' : '13px', margin: '4px 0',
        paddingLeft: (item.startsWith('-') || item.startsWith('•') || item.startsWith('*')) ? '10px' : '0',
        color: isCarryover ? '#999' : '#444',
        fontStyle: isCarryover ? 'italic' : 'normal'
      }}>
        {(item.startsWith('-') || item.startsWith('•') || item.startsWith('*')) ? `· ${text}` : text}
      </p>
    );
  });

  return (
    <div>
      {sections.map((section, i) => {
        if (section.type === 'week_title') return (
          <p key={i} style={{ fontSize: '12px', color: '#888', margin: '0 0 16px', fontWeight: '500' }}>{section.text}</p>
        );
        if (section.type === 'day') {
          if (section.holiday) return (
            <div key={i} style={{ background: '#f9f9f9', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: '14px 20px', marginBottom: '10px' }}>
              <p style={{ fontSize: '13px', fontWeight: '500', color: '#bbb', margin: 0 }}>{section.header}</p>
            </div>
          );
          const header = section.header;
          const todayPT = ['Domingo','Segunda-feira','Terca-feira','Quarta-feira','Quinta-feira','Sexta-feira','Sabado'][new Date().getDay()];
          const isToday = header.includes(todayPT) || (todayPT === 'Terca-feira' && header.includes('Terça'));
          const borderColor = isToday ? '#1a73e8' : '#ddd';
          return (
            <div key={i} style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderLeft: `3px solid ${borderColor}`, borderRadius: '12px', padding: '16px 20px', marginBottom: '10px' }}>
              <p style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a1a', margin: '0 0 10px' }}>{section.header}</p>
              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '10px' }}>
                {renderItems(section.items, false)}
              </div>
            </div>
          );
        }
        if (section.type === 'other') {
          const isAguarda = section.header.toUpperCase().includes('AGUARDA');
          const isHousekeeping = section.header.toUpperCase().includes('ASANA') || section.header.toUpperCase().includes('HOUSEKEEPING');
          const isNotes = section.header.toUpperCase().includes('NOTES');
          const headerColor = isAguarda ? '#888' : isHousekeeping ? '#e67e22' : isNotes ? '#1a73e8' : '#27ae60';
          return (
            <div key={i} style={{ background: '#f0f4f8', border: '0.5px solid #dce3ea', borderRadius: '12px', padding: '16px 20px', marginBottom: '10px' }}>
              <p style={{ fontSize: '11px', fontWeight: '600', color: headerColor, margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{section.header}</p>
              {renderItems(section.items, true)}
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

function StatusBadge({ label, count, color }) {
  if (!count) return null;
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '500', marginRight: '6px', background: color + '18', color }}>
      {count} {label}
    </span>
  );
}

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState('');
  const [token, setTokenVal] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!email || !token) { setError('Email and token required'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${BACKEND}/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, me_token: token })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Login failed'); }
      else { setToken(data.token); onLogin(data); }
    } catch { setError('Could not reach server'); }
    setLoading(false);
  };

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f0f0' }}>
      <div style={{ background: '#fff', borderRadius: '12px', padding: '40px 48px', width: '420px', boxShadow: '0 2px 16px rgba(0,0,0,0.08)', border: '0.5px solid #e0e0e0' }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <img src="/ricoh-logo.png" alt="Ricoh" style={{ height: '48px' }} />
        </div>
        <h2 style={{ textAlign: 'center', fontSize: '20px', fontWeight: '700', margin: '0 0 8px', color: '#1a1a1a' }}>PRIMO</h2>
        <p style={{ textAlign: 'center', fontSize: '12px', color: '#888', margin: '0 0 6px' }}>Project Intelligence &amp; Management Operator</p>
        <p style={{ textAlign: 'center', fontSize: '14px', color: '#444', margin: '0 0 28px' }}>Sign in with your ManageEngine credentials</p>
        <div style={{ marginBottom: '16px' }}>
          <p style={{ fontSize: '13px', color: '#333', margin: '0 0 6px', fontWeight: '500' }}>Email Address</p>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your.email@ricoh.pt"
            style={{ width: '100%', padding: '11px 14px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none', boxSizing: 'border-box', background: '#fffff0', color: '#333' }} />
        </div>
        <div style={{ marginBottom: '8px' }}>
          <p style={{ fontSize: '13px', color: '#333', margin: '0 0 6px', fontWeight: '500' }}>API Key</p>
          <input type="password" value={token} onChange={e => setTokenVal(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleLogin()} placeholder="Your ME API token"
            style={{ width: '100%', padding: '11px 14px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none', boxSizing: 'border-box', background: '#fffff0', color: '#333' }} />
        </div>
        <p style={{ fontSize: '11px', color: '#c00', margin: '0 0 20px' }}>Generate your API key in ManageEngine: User Profile → Generate Authtoken</p>
        {error && <p style={{ color: '#c00', fontSize: '12px', margin: '0 0 12px' }}>{error}</p>}
        <button onClick={handleLogin} disabled={loading}
          style={{ width: '100%', padding: '12px', fontSize: '14px', fontWeight: '600', background: '#cc0000', color: '#fff', border: 'none', borderRadius: '6px', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.8 : 1 }}>
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
        <p style={{ textAlign: 'center', fontSize: '11px', color: '#aaa', margin: '20px 0 0' }}>This application is secure and uses encryption for all communications.</p>
      </div>
    </div>
  );
}

function DayWatch({ member }) {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [watchRes, alertsRes] = await Promise.all([
        apiFetch(`/daywatch/${member.gid}`),
        apiFetch(`/alerts/${member.gid}`)
      ]);
      const watchData = await watchRes.json();
      const alertsData = await alertsRes.json();
      if (!watchData.error) setData(watchData);
      if (!alertsData.error) setAlerts(alertsData.alerts || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [member.gid]);

  const handleGenerate = async () => {
    setGenerating(true);
    try { await apiFetch('/daywatch/generate'); await load(); } catch {}
    setGenerating(false);
  };

  const critical = alerts.filter(a => a.severity === 'critical');
  const high = alerts.filter(a => a.severity === 'high');
  const [alertsExpanded, setAlertsExpanded] = useState(false);
  const totalAlerts = critical.length + high.length;
  const visibleCritical = alertsExpanded ? critical : critical.slice(0, 3);
  const visibleHigh = alertsExpanded ? high : high.slice(0, Math.max(0, 5 - critical.length));

  return (
    <div>
      {(critical.length > 0 || high.length > 0) && (
        <div style={{ background: critical.length > 0 ? '#fff5f5' : '#fff8f0', border: `1px solid ${critical.length > 0 ? '#ffcccc' : '#ffe0b2'}`, borderRadius: '10px', padding: '12px 16px', marginBottom: '16px' }}>
          <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: '500', color: critical.length > 0 ? '#c0392b' : '#e67e22' }}>
            {critical.length > 0 ? `${critical.length} critical alert${critical.length > 1 ? 's' : ''} this week` : `${high.length} high priority alert${high.length > 1 ? 's' : ''} this week`}
          </p>
          {visibleCritical.map((a, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', margin: '4px 0' }}>
              <p style={{ margin: 0, fontSize: '12px', color: '#c0392b', flex: 1 }}>· {a.message}</p>
              {a.id && <button onClick={async () => {
                await apiFetch(`/alerts/${a.id}/resolve`, { method: 'POST' });
                setAlerts(prev => prev.filter(x => x.id !== a.id));
              }} style={{ fontSize: '10px', color: '#aaa', background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 0 8px', flexShrink: 0 }}>resolve</button>}
            </div>
          ))}
          {visibleHigh.map((a, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', margin: '4px 0' }}>
              <p style={{ margin: 0, fontSize: '12px', color: '#e67e22', flex: 1 }}>· {a.message}</p>
              {a.id && <button onClick={async () => {
                await apiFetch(`/alerts/${a.id}/resolve`, { method: 'POST' });
                setAlerts(prev => prev.filter(x => x.id !== a.id));
              }} style={{ fontSize: '10px', color: '#aaa', background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 0 8px', flexShrink: 0 }}>resolve</button>}
            </div>
          ))}
          {totalAlerts > 5 && (
            <p onClick={() => setAlertsExpanded(!alertsExpanded)}
              style={{ margin: '6px 0 0', fontSize: '11px', color: '#1a73e8', cursor: 'pointer', userSelect: 'none' }}>
              {alertsExpanded ? '\u25b2 Show less' : `\u25bc +${totalAlerts - 5} more`}
            </p>
          )}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div>
          {data && <p style={{ margin: 0, fontSize: '11px', color: '#aaa' }}>
            {data.generated_at?.replace('T', ' ').split('.')[0]}
            {data.content?.me_tickets_with_activity !== undefined && ` · ${data.content.me_tickets_with_activity} ME tickets with activity`}
          </p>}
        </div>
        <button onClick={handleGenerate} disabled={generating} style={{ padding: '7px 14px', fontSize: '12px', border: '0.5px solid #ddd', borderRadius: '8px', background: '#fff', cursor: 'pointer' }}>
          {generating ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>
      {loading && <p style={{ color: '#aaa', fontSize: '13px' }}>Loading...</p>}
      {!loading && !data && (
        <div style={{ background: '#f9f9f9', borderRadius: '12px', padding: '32px', textAlign: 'center' }}>
          <p style={{ color: '#aaa', fontSize: '13px', margin: '0 0 12px' }}>No Day Watch yet.</p>
          <button onClick={handleGenerate} disabled={generating} style={{ padding: '8px 16px', fontSize: '13px', border: '0.5px solid #ddd', borderRadius: '8px', background: '#fff', cursor: 'pointer' }}>
            {generating ? 'Generating...' : 'Generate now'}
          </button>
        </div>
      )}
      {data && (
        <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: '20px 24px' }}>
          {formatText(data.content?.watch)}
        </div>
      )}
    </div>
  );
}

function PlanCard({ member }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch(`/plans/${member.gid}`)
      .then(r => r && r.json())
      .then(d => { setPlan(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [member.gid]);

  return (
    <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: '20px 24px', marginBottom: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
        <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: member.role === 'lead' ? '#e8f0fe' : '#f0f4f8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '500', color: member.role === 'lead' ? '#1a73e8' : '#444' }}>
          {member.name.split(' ').map(n => n[0]).join('')}
        </div>
        <div>
          <p style={{ margin: 0, fontWeight: '500', fontSize: '14px' }}>{member.name}</p>
          <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>
            {member.role === 'lead' ? 'Team Lead' : 'Project Manager'}
            {plan && ` · ${plan.plan?.project_count} projects · issued ${plan.generated_at ? plan.generated_at.split(' ')[0] : plan.week_start}`}
          </p>
        </div>
      </div>
      {loading && <p style={{ color: '#aaa', fontSize: '13px' }}>Loading...</p>}
      {plan && !plan.error && (
        <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '16px' }}>
          {formatPlan(plan.plan?.plan)}
        </div>
      )}
    </div>
  );
}

function TeamOverview() {
  const [pulse, setPulse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch('/pulse/latest').then(r => r && r.json()).then(d => { setPulse(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <p style={{ color: '#aaa', fontSize: '13px' }}>Loading...</p>;
  if (!pulse || pulse.error) return <p style={{ color: '#aaa', fontSize: '13px' }}>No data yet.</p>;
  const summaries = pulse.content?.summaries || [];

  return (
    <div>
      {summaries.map(s => (
        <div key={s.name} style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: '20px 24px', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <div>
              <p style={{ margin: 0, fontWeight: '500', fontSize: '14px' }}>{s.name}</p>
              <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>{s.total} active projects</p>
            </div>
            <div>
              <StatusBadge label="overdue" count={s.overdue_count} color="#c0392b" />
              <StatusBadge label="at risk" count={s.at_risk_count} color="#e67e22" />
              <StatusBadge label="on hold" count={s.on_hold_count} color="#888" />
              <StatusBadge label="on track" count={s.on_track_count} color="#27ae60" />
            </div>
          </div>
          {s.overdue.length > 0 && (
            <div style={{ marginTop: '8px' }}>
              <p style={{ fontSize: '11px', color: '#c0392b', fontWeight: '500', margin: '0 0 4px', textTransform: 'uppercase' }}>Overdue</p>
              {s.overdue.map((p, i) => <p key={i} style={{ fontSize: '12px', margin: '2px 0', color: '#444', paddingLeft: '8px' }}>· {p}</p>)}
            </div>
          )}
          {s.at_risk.length > 0 && (
            <div style={{ marginTop: '8px' }}>
              <p style={{ fontSize: '11px', color: '#e67e22', fontWeight: '500', margin: '0 0 4px', textTransform: 'uppercase' }}>At risk</p>
              {s.at_risk.map((p, i) => <p key={i} style={{ fontSize: '12px', margin: '2px 0', color: '#444', paddingLeft: '8px' }}>· {p}</p>)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function TeamPulse() {
  const [pulse, setPulse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = () => {
    apiFetch('/pulse/latest').then(r => r && r.json()).then(d => { setPulse(d); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    await apiFetch('/pulse/generate');
    load();
    setGenerating(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>{pulse && !pulse.error && <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>Generated {pulse.generated_at?.split('.')[0].replace('T', ' ')}</p>}</div>
        <button onClick={handleGenerate} disabled={generating} style={{ padding: '8px 16px', fontSize: '13px', border: '0.5px solid #ddd', borderRadius: '8px', background: '#fff', cursor: 'pointer' }}>
          {generating ? 'Generating...' : 'Refresh pulse'}
        </button>
      </div>
      {loading && <p style={{ color: '#aaa', fontSize: '13px' }}>Loading...</p>}
      {!loading && (!pulse || pulse.error) && <p style={{ color: '#aaa', fontSize: '13px' }}>No Team Pulse yet.</p>}
      {pulse && !pulse.error && (
        <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: '20px 24px' }}>
          {formatText(pulse.content?.pulse)}
        </div>
      )}
    </div>
  );
}

function TranscriptCard({ transcript: t, onDelete }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
          <p style={{ fontSize: '14px', fontWeight: '600', margin: '0 0 3px', color: '#1a1a1a' }}>
            {t.meeting_date
              ? new Date(t.meeting_date + 'T12:00:00').toLocaleDateString('pt-PT', { day: 'numeric', month: 'long', year: 'numeric' })
              : 'Date unknown'}
          </p>
          <p style={{ fontSize: '12px', color: '#888', margin: '0 0 2px' }}>
            {t.participants?.length > 0 ? t.participants.slice(0, 4).join(', ') + (t.participants.length > 4 ? ` +${t.participants.length - 4}` : '') : 'Participants unknown'}
          </p>
          <p style={{ fontSize: '11px', color: '#aaa', margin: 0 }}>
            {t.decisions?.length > 0 ? `${t.decisions.length} decision${t.decisions.length !== 1 ? 's' : ''}` : ''}
            {t.decisions?.length > 0 && t.actions?.length > 0 ? ' · ' : ''}
            {t.actions?.length > 0 ? `${t.actions.length} action${t.actions.length !== 1 ? 's' : ''}` : ''}
            {(!t.decisions?.length && !t.actions?.length) ? 'No decisions or actions recorded' : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', marginLeft: '12px', flexShrink: 0 }}>
          <button onClick={() => setExpanded(!expanded)}
            style={{ fontSize: '11px', color: '#1a73e8', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px' }}>
            {expanded ? '▲ Less' : '▼ More'}
          </button>
          <button onClick={() => onDelete(t.id)}
            style={{ fontSize: '11px', color: '#c0392b', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px' }}>
            Delete
          </button>
        </div>
      </div>

      {expanded && (
        <div style={{ marginTop: '12px', padding: '14px', background: '#f8f9fa', borderRadius: '8px' }}>
          {t.summary && (
            <div style={{ marginBottom: '12px' }}>
              <p style={{ fontSize: '11px', fontWeight: '600', color: '#1a73e8', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Sumário</p>
              <p style={{ fontSize: '13px', color: '#444', margin: 0, lineHeight: '1.6' }}>{t.summary}</p>
            </div>
          )}
          {t.decisions?.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <p style={{ fontSize: '11px', fontWeight: '600', color: '#c0392b', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Decisões ({t.decisions.length})</p>
              {t.decisions.map((d, i) => <p key={i} style={{ fontSize: '12px', margin: '3px 0', paddingLeft: '8px', color: '#444' }}>• {d}</p>)}
            </div>
          )}
          {t.actions?.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <p style={{ fontSize: '11px', fontWeight: '600', color: '#e67e22', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ações ({t.actions.length})</p>
              {(() => {
                const byOwner = {};
                t.actions.forEach(a => {
                  const owner = a.owner_full_name || 'Equipa';
                  if (!byOwner[owner]) byOwner[owner] = [];
                  byOwner[owner].push(a);
                });
                return Object.entries(byOwner).sort(([a],[b]) => a === 'Equipa' ? 1 : b === 'Equipa' ? -1 : a.localeCompare(b)).map(([owner, actions]) => (
                  <div key={owner} style={{ marginBottom: '8px' }}>
                    <p style={{ fontSize: '12px', fontWeight: '500', margin: '0 0 3px', color: '#333' }}>{owner}</p>
                    {actions.map((a, i) => <p key={i} style={{ fontSize: '12px', margin: '2px 0', paddingLeft: '12px', color: '#555' }}>• {a.text}{a.due ? ` (${a.due})` : ''}</p>)}
                  </div>
                ));
              })()}
            </div>
          )}
          {t.open_questions?.length > 0 && (
            <div style={{ marginBottom: '8px' }}>
              <p style={{ fontSize: '11px', fontWeight: '600', color: '#888', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Questões em aberto ({t.open_questions.length})</p>
              {t.open_questions.map((q, i) => <p key={i} style={{ fontSize: '12px', margin: '3px 0', paddingLeft: '8px', color: '#666' }}>· {q}</p>)}
            </div>
          )}
          <p style={{ fontSize: '11px', color: '#ccc', margin: '8px 0 0' }}>{t.filename} · Uploaded {t.uploaded_at?.split('T')[0]}</p>
        </div>
      )}
    </div>
  );
}

function Projects({ user, projects }) {
  const [selectedProject, setSelectedProject] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState([]);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [copied, setCopied] = useState(false);
  const [hours, setHours] = useState(null);
  const [hoursLoading, setHoursLoading] = useState(false);
  const [briefing, setBriefing] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [briefingCollapsed, setBriefingCollapsed] = useState(false);

  const myProjects = projects.filter(p => p.owner_gid === user.gid);

  const handleBriefing = async (projectGid) => {
    setBriefingLoading(true);
    setBriefing(null);
    try {
      const r = await apiFetch(`/briefing/${projectGid}`);
      const d = await r.json();
      setBriefing(d);
    } catch {}
    setBriefingLoading(false);
  };

  const loadHistory = async (projectGid) => {
    if (!projectGid) return;
    try {
      const r = await apiFetch(`/transcripts/${projectGid}`);
      const d = await r.json();
      setHistory(d.transcripts || []);
    } catch {}
  };

  const handleProjectChange = async (gid) => {
    setSelectedProject(gid);
    setResult(null);
    setCreated([]);
    setSuggestions([]);
    setHours(null);
    setBriefing(null);
    setBriefingCollapsed(false);
    loadHistory(gid);
    if (gid) {
      setHoursLoading(true);
      try {
        const r = await apiFetch(`/hours/${gid}`);
        const d = await r.json();
        setHours(d);
      } catch {}
      setHoursLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!file || !selectedProject) { setError('Select a project and a file first'); return; }
    setUploading(true); setError(''); setResult(null); setCreated([]);
    const project = myProjects.find(p => p.gid === selectedProject);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('project_gid', selectedProject);
    formData.append('project_name', project?.name || selectedProject);
    try {
      const res = await fetch(`${BACKEND}/transcripts/upload`, { method: 'POST', headers: authHeaders(), body: formData });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Upload failed'); }
      else {
        setResult(data);
        setSuggestions((data.asana_suggestions || []).map((s, i) => ({
          ...s, id: i, selected: s.type === 'create', editedText: s.action_text || s.existing_task_name || ''
        })));
      }
    } catch { setError('Upload failed — check connection'); }
    setUploading(false);
  };

  const buildEmailHtml = () => {
    if (!result) return '';
    const parts = [];
    const dateStr = result.meeting_date
      ? new Date(result.meeting_date + 'T12:00:00').toLocaleDateString('pt-PT', { day: 'numeric', month: 'long', year: 'numeric' })
      : '';
    if (dateStr) parts.push(`<p style="margin:0 0 2px 0">Data: ${dateStr}</p>`);
    if (result.participants?.length > 0) parts.push(`<p style="margin:0 0 16px 0">Participantes: ${result.participants.join(', ')}</p>`);
    if (result.summary_pt) {
      parts.push(`<p style="margin:0 0 4px 0"><strong>Sumário:</strong></p>`);
      parts.push(`<p style="margin:0 0 16px 0">${result.summary_pt}</p>`);
    }
    if (result.decisions?.length > 0) {
      parts.push(`<p style="margin:0 0 4px 0"><strong>Decisões:</strong></p>`);
      parts.push('<ul style="margin:0 0 16px 0;padding-left:32px">');
      result.decisions.forEach(d => parts.push(`<li style="margin-bottom:4px">${d}</li>`));
      parts.push('</ul>');
    }
    if (result.actions?.length > 0) {
      parts.push(`<p style="margin:0 0 4px 0"><strong>Ações:</strong></p>`);
      const byOwner = {};
      result.actions.forEach(a => {
        const owner = a.owner_full_name || 'Equipa';
        if (!byOwner[owner]) byOwner[owner] = [];
        byOwner[owner].push(a);
      });
      const sortedOwners = Object.entries(byOwner).sort(([a], [b]) => {
        if (a === 'Equipa') return 1;
        if (b === 'Equipa') return -1;
        return a.localeCompare(b);
      });
      sortedOwners.forEach(([owner, actions]) => {
        parts.push(`<p style="margin:8px 0 4px 0;padding-left:16px">${owner}</p>`);
        parts.push('<ul style="margin:0 0 8px 0;padding-left:48px">');
        actions.forEach(a => {
          const due = a.due ? ` <span style="color:#888">(${a.due})</span>` : '';
          parts.push(`<li style="margin-bottom:4px">${a.text}${due}</li>`);
        });
        parts.push('</ul>');
      });
    }
    if (result.open_questions?.length > 0) {
      parts.push(`<p style="margin:16px 0 4px 0"><strong>Questões em aberto:</strong></p>`);
      parts.push('<ul style="margin:0;padding-left:32px">');
      result.open_questions.forEach(q => parts.push(`<li style="margin-bottom:4px">${q}</li>`));
      parts.push('</ul>');
    }
    return parts.join('\n');
  };

  const handleCopy = () => {
    const html = buildEmailHtml();
    const blob = new Blob([html], { type: 'text/html' });
    const textBlob = new Blob([html.replace(/<[^>]+>/g, '')], { type: 'text/plain' });
    const item = new ClipboardItem({ 'text/html': blob, 'text/plain': textBlob });
    navigator.clipboard.write([item]).catch(() => {
      navigator.clipboard.writeText(html.replace(/<[^>]+>/g, ''));
    });
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleApplySuggestions = async () => {
    setCreating(true);
    const createdTasks = [];
    for (const s of suggestions) {
      if (!s.selected) continue;
      try {
        if (s.type === 'create') {
          const r = await apiFetch('/asana/tasks/create', {
            method: 'POST',
            body: JSON.stringify({
              project_gid: selectedProject, name: s.editedText,
              notes: `Criado a partir de transcrição: ${result.filename}${s.owner ? `\nResponsável: ${s.owner}` : ''}`,
              due_on: s.due || null, transcript_id: result.transcript_id
            })
          });
          const d = await r.json();
          if (d.task?.gid) createdTasks.push({ type: 'create', name: s.editedText });
        }
      } catch {}
    }
    setCreated(createdTasks);
    setCreating(false);
    loadHistory(selectedProject);
  };

  const toggleSuggestion = (id) => setSuggestions(prev => prev.map(s => s.id === id ? { ...s, selected: !s.selected } : s));
  const editSuggestion = (id, text) => setSuggestions(prev => prev.map(s => s.id === id ? { ...s, editedText: text } : s));
  const selectedCount = suggestions.filter(s => s.selected).length;

  return (
    <div>
      {/* Project selector */}
      <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: '20px 24px', marginBottom: '16px' }}>
        <div style={{ marginBottom: '4px' }}>
          <p style={{ fontSize: '12px', color: '#444', margin: '0 0 6px' }}>Select Project</p>
          <select value={selectedProject} onChange={e => handleProjectChange(e.target.value)}
            style={{ width: '100%', padding: '9px 12px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none', background: '#fff' }}>
            <option value="">Select a project...</option>
            {myProjects.map(p => <option key={p.gid} value={p.gid}>{p.name}</option>)}
          </select>
        </div>
      </div>

      {/* Hours */}
      {hoursLoading && (
        <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: '16px 20px', marginBottom: '16px' }}>
          <p style={{ fontSize: '12px', color: '#aaa', margin: 0 }}>Loading hours...</p>
        </div>
      )}
      {hours && !hours.error && (
        <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: '16px 20px', marginBottom: '16px' }}>
          <p style={{ fontSize: '11px', fontWeight: '600', color: '#444', margin: '0 0 12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Hours</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
            <div>
              <p style={{ fontSize: '24px', fontWeight: '600', margin: '0', color: hours.percentage_consumed > 90 ? '#c0392b' : hours.percentage_consumed > 70 ? '#e67e22' : '#27ae60' }}>
                {hours.percentage_consumed != null ? `${hours.percentage_consumed}%` : 'N/A'}
              </p>
              <p style={{ fontSize: '11px', color: '#aaa', margin: '0' }}>consumed</p>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ background: '#f0f0f0', borderRadius: '4px', height: '8px', overflow: 'hidden', marginBottom: '6px' }}>
                <div style={{ height: '100%', borderRadius: '4px', width: `${Math.min(hours.percentage_consumed || 0, 100)}%`, background: hours.percentage_consumed > 90 ? '#c0392b' : hours.percentage_consumed > 70 ? '#e67e22' : '#27ae60' }} />
              </div>
              <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>
                {hours.total_logged_hours}h logged of {hours.total_estimated_hours}h estimated
                {hours.remaining_hours != null && ` · ${hours.remaining_hours}h remaining`}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: '#888', borderTop: '1px solid #f0f0f0', paddingTop: '10px' }}>
            <span>PM: {hours.estimated?.pm_hours}h</span>
            <span>Specialist: {hours.estimated?.specialist_hours}h</span>
            <span>Field: {hours.estimated?.field_hours}h</span>
            <span style={{ marginLeft: 'auto' }}>{hours.logged?.last_log_date ? `Last log: ${hours.logged.last_log_date}` : 'No logs yet'}</span>
          </div>
          {hours.incident_hours > 0 && (
            <div style={{ marginTop: '10px', padding: '8px 12px', background: '#fff5f5', borderRadius: '8px', border: '0.5px solid #ffd0d0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '12px', color: '#c0392b' }}>⚠ Incidents logged</span>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#c0392b' }}>{hours.incident_hours}h · {hours.incident_rate}% of consumed</span>
            </div>
          )}
        </div>
      )}
      {hours?.error && (
        <div style={{ background: '#fff8f0', border: '0.5px solid #ffe0b2', borderRadius: '12px', padding: '12px 16px', marginBottom: '16px' }}>
          <p style={{ fontSize: '12px', color: '#e67e22', margin: 0 }}>Could not load hours: {hours.error}</p>
        </div>
      )}

      {/* Brief me */}
      {selectedProject && (
        <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: '16px 20px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <p style={{ fontSize: '13px', fontWeight: '500', margin: '0 0 2px' }}>Pre-Meeting Briefing</p>
            <p style={{ fontSize: '11px', color: '#aaa', margin: 0 }}>Pre-meeting intelligence for this project</p>
          </div>
          <button onClick={() => handleBriefing(selectedProject)} disabled={briefingLoading}
            style={{ padding: '8px 16px', fontSize: '12px', fontWeight: '500', background: briefingLoading ? '#eee' : '#1a73e8', color: briefingLoading ? '#888' : '#fff', border: 'none', borderRadius: '6px', cursor: briefingLoading ? 'not-allowed' : 'pointer' }}>
            {briefingLoading ? 'Generating...' : 'Brief me'}
          </button>
        </div>
      )}

      {briefing && !briefing.error && (
        <div style={{ background: '#f0f4ff', border: '0.5px solid #c5cae9', borderRadius: '12px', padding: '16px 20px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: briefingCollapsed ? 0 : '8px', cursor: 'pointer' }}
            onClick={() => setBriefingCollapsed(!briefingCollapsed)}>
            <p style={{ fontSize: '12px', fontWeight: '600', color: '#1a73e8', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pre-Meeting Briefing</p>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <p style={{ fontSize: '11px', color: '#888', margin: 0 }}>{briefing.due_str}</p>
              {briefing.open_items > 0 && <span style={{ fontSize: '11px', color: '#c0392b', fontWeight: '500' }}>{briefing.open_items} open</span>}
              <span style={{ fontSize: '12px', color: '#1a73e8' }}>{briefingCollapsed ? '▼' : '▲'}</span>
            </div>
          </div>
          {!briefingCollapsed && (
            <>
              {briefing.current_note && <p style={{ fontSize: '12px', color: '#666', margin: '0 0 12px', fontStyle: 'italic' }}>PM Note: {briefing.current_note}</p>}
              <div style={{ fontSize: '13px', lineHeight: '1.6' }}>{formatText(briefing.briefing)}</div>
            </>
          )}
        </div>
      )}

      {/* Upload transcript */}
      {selectedProject && (
        <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: '20px 24px', marginBottom: '16px' }}>
          <p style={{ fontSize: '12px', fontWeight: '600', color: '#444', margin: '0 0 12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Upload Transcript</p>
          <div style={{ marginBottom: '12px' }}>
            <input type="file" accept=".txt,.vtt,.docx" onChange={e => setFile(e.target.files[0])} style={{ fontSize: '13px', color: '#444' }} />
            <p style={{ fontSize: '11px', color: '#aaa', margin: '4px 0 0' }}>Export transcript from Teams as .vtt file</p>
          </div>
          {error && <p style={{ color: '#c00', fontSize: '12px', margin: '0 0 12px' }}>{error}</p>}
          <button onClick={handleUpload} disabled={uploading || !file}
            style={{ padding: '9px 20px', fontSize: '13px', fontWeight: '500', background: uploading ? '#eee' : '#cc0000', color: uploading ? '#888' : '#fff', border: 'none', borderRadius: '6px', cursor: uploading ? 'not-allowed' : 'pointer' }}>
            {uploading ? 'A processar...' : 'Upload & Process'}
          </button>
        </div>
      )}

      {/* Transcript results */}
      {result && (
        <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: '20px 24px', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '500', margin: '0 0 4px' }}>{result.project_name}</h3>
          <p style={{ fontSize: '11px', color: '#aaa', margin: '0 0 16px' }}>
            {result.filename}{result.participants?.length > 0 && ` · ${result.participants.join(', ')}`}
          </p>
          <div style={{ background: '#f8f9fe', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px' }}>
            <p style={{ fontSize: '11px', fontWeight: '500', color: '#1a73e8', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Summary</p>
            <p style={{ fontSize: '13px', margin: 0, lineHeight: '1.6' }}>{result.summary_pt}</p>
          </div>
          {result.decisions?.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <p style={{ fontSize: '11px', fontWeight: '500', color: '#c0392b', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Decisions ({result.decisions.length})</p>
              {result.decisions.map((d, i) => <p key={i} style={{ margin: '4px 0', paddingLeft: '8px', fontSize: '13px' }}>• {d}</p>)}
            </div>
          )}
          {result.actions?.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <p style={{ fontSize: '11px', fontWeight: '500', color: '#e67e22', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Actions</p>
              {(() => {
                const byOwner = {};
                result.actions.forEach(a => { const owner = a.owner_full_name || 'Equipa'; if (!byOwner[owner]) byOwner[owner] = []; byOwner[owner].push(a); });
                return Object.entries(byOwner).sort(([a],[b]) => a === 'Equipa' ? 1 : b === 'Equipa' ? -1 : a.localeCompare(b)).map(([owner, actions]) => (
                  <div key={owner} style={{ marginBottom: '10px' }}>
                    <p style={{ fontWeight: '500', fontSize: '13px', margin: '0 0 4px' }}>{owner}</p>
                    {actions.map((a, i) => <p key={i} style={{ margin: '3px 0', paddingLeft: '12px', fontSize: '13px' }}>• {a.text}{a.due ? ` (${a.due})` : ''}</p>)}
                  </div>
                ));
              })()}
            </div>
          )}
          {result.open_questions?.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <p style={{ fontSize: '11px', fontWeight: '500', color: '#888', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Open Questions</p>
              {result.open_questions.map((q, i) => <p key={i} style={{ fontSize: '13px', margin: '4px 0', paddingLeft: '8px' }}>· {q}</p>)}
            </div>
          )}
          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={handleCopy}
              style={{ padding: '7px 14px', fontSize: '12px', border: '0.5px solid #ddd', borderRadius: '8px', background: copied ? '#f0fff4' : '#fff', color: copied ? '#27ae60' : '#444', cursor: 'pointer' }}>
              {copied ? '✓ Copied' : 'Copy for email'}
            </button>
          </div>
        </div>
      )}

      {/* Asana suggestions */}
      {suggestions.length > 0 && (
        <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: '20px 24px', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', margin: '0 0 4px' }}>Asana Intelligence</h3>
          <p style={{ fontSize: '11px', color: '#aaa', margin: '0 0 16px' }}>Review suggestions and confirm what to create or update</p>
          {suggestions.map(s => (
            <div key={s.id} style={{ border: `1px solid ${s.selected ? (s.type === 'create' ? '#c3e6cb' : s.type === 'close' ? '#f5c6cb' : '#ffe0b2') : '#eee'}`, borderRadius: '8px', padding: '12px 14px', marginBottom: '8px', background: s.selected ? (s.type === 'create' ? '#f0fff4' : s.type === 'close' ? '#fff5f5' : '#fff8f0') : '#fafafa' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                <div onClick={() => toggleSuggestion(s.id)} style={{ width: '16px', height: '16px', borderRadius: '4px', flexShrink: 0, cursor: 'pointer', background: s.selected ? '#cc0000' : '#fff', border: `1.5px solid ${s.selected ? '#cc0000' : '#ddd'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {s.selected && <span style={{ color: '#fff', fontSize: '10px', fontWeight: '700' }}>✓</span>}
                </div>
                <span style={{ fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', color: s.type === 'create' ? '#27ae60' : s.type === 'close' ? '#c0392b' : '#e67e22', background: s.type === 'create' ? '#e8f5e9' : s.type === 'close' ? '#fdecea' : '#fff3e0', padding: '2px 8px', borderRadius: '10px' }}>
                  {s.type === 'create' ? 'New task' : s.type === 'close' ? 'Close task' : 'Update task'}
                </span>
                {s.owner && <span style={{ fontSize: '11px', color: '#888' }}>{s.owner}</span>}
                {s.due && <span style={{ fontSize: '11px', color: '#888' }}>{s.due}</span>}
              </div>
              {s.type === 'create' ? (
                <input value={s.editedText} onChange={e => editSuggestion(s.id, e.target.value)}
                  style={{ width: '100%', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', padding: '6px 10px', outline: 'none', boxSizing: 'border-box', background: '#fff' }} />
              ) : (
                <div>
                  <p style={{ fontSize: '12px', color: '#666', margin: '0 0 2px' }}>Existing task: <strong>{s.existing_task_name}</strong></p>
                  <p style={{ fontSize: '13px', margin: 0 }}>{s.editedText}</p>
                </div>
              )}
              <p style={{ fontSize: '11px', color: '#aaa', margin: '6px 0 0' }}>{s.reason}</p>
            </div>
          ))}
          {created.length > 0 ? (
            <div style={{ background: '#f0fff4', border: '1px solid #c3e6cb', borderRadius: '8px', padding: '12px 16px', marginTop: '12px' }}>
              <p style={{ fontSize: '12px', fontWeight: '500', color: '#27ae60', margin: '0 0 6px' }}>{created.length} task{created.length > 1 ? 's' : ''} created in Asana</p>
              {created.map((t, i) => <p key={i} style={{ fontSize: '12px', margin: '2px 0', color: '#27ae60' }}>✓ {t.name}</p>)}
            </div>
          ) : (
            <button onClick={handleApplySuggestions} disabled={creating || selectedCount === 0}
              style={{ marginTop: '8px', padding: '9px 20px', fontSize: '13px', fontWeight: '500', background: selectedCount === 0 ? '#eee' : '#1a1a1a', color: selectedCount === 0 ? '#888' : '#fff', border: 'none', borderRadius: '6px', cursor: selectedCount === 0 ? 'not-allowed' : 'pointer' }}>
              {creating ? 'Creating...' : `Apply ${selectedCount} suggestion${selectedCount !== 1 ? 's' : ''}`}
            </button>
          )}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: '20px 24px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '500', margin: '0 0 12px' }}>Previous Transcripts ({history.length})</h3>
          {history.map(t => (
            <TranscriptCard key={t.id} transcript={t} onDelete={async (id) => {
              if (!window.confirm('Delete this transcript?')) return;
              await apiFetch(`/transcripts/${id}`, { method: 'DELETE' });
              setHistory(prev => prev.filter(x => x.id !== id));
            }} />
          ))}
        </div>
      )}
    </div>
  );
}

function ActionConfirmCard({ action, executing, onConfirm, onCancel, onChange }) {
  console.log("ActionConfirmCard action:", JSON.stringify(action));
  const update = (field, value) => onChange(prev => ({ ...prev, [field]: value }));

  return (
    <div style={{ background: '#fff8f0', border: '1px solid #ffe0b2', borderRadius: '12px', padding: '16px 20px', marginBottom: '12px' }}>
      <p style={{ fontSize: '12px', fontWeight: '600', color: '#e67e22', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        Confirm Action
      </p>
      <p style={{ fontSize: '12px', color: '#888', margin: '0 0 16px' }}>
        Review and edit before confirming
      </p>

      {/* Confirmation message */}
      {action.confirmation_message && (
        <p style={{ fontSize: '13px', color: '#444', margin: '0 0 12px', lineHeight: '1.5' }}>{action.confirmation_message}</p>
      )}

      {/* Project — read only */}
      {action.project_name && (
        <div style={{ marginBottom: '10px' }}>
          <p style={{ fontSize: '11px', color: '#888', margin: '0 0 3px' }}>Project</p>
          <p style={{ fontSize: '13px', color: '#444', margin: 0, fontWeight: '500' }}>{action.project_name}</p>
        </div>
      )}

      {/* Task name */}
      {action.action_type === 'create_task' && (
        <div style={{ marginBottom: '10px' }}>
          <p style={{ fontSize: '11px', color: '#888', margin: '0 0 3px' }}>Task name</p>
          <input
            value={action.task_name || ''}
            onChange={e => update('task_name', e.target.value)}
            style={{ width: '100%', padding: '8px 10px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none', boxSizing: 'border-box' }}
          />
        </div>
      )}
      {action.action_type !== 'create_task' && action.task_name && (
        <div style={{ marginBottom: '10px' }}>
          <p style={{ fontSize: '11px', color: '#888', margin: '0 0 3px' }}>Task</p>
          <input
            value={action.task_name}
            onChange={e => update('task_name', e.target.value)}
            style={{ width: '100%', padding: '8px 10px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none', boxSizing: 'border-box' }}
          />
        </div>
      )}

      {/* Assignee — editable */}
      <div style={{ marginBottom: '10px' }}>
        <p style={{ fontSize: '11px', color: '#888', margin: '0 0 3px' }}>Assignee</p>
        <input
          value={action.assignee_name || ''}
          onChange={e => update('assignee_name', e.target.value)}
          placeholder="Name (leave blank to leave unassigned)"
          style={{ width: '100%', padding: '8px 10px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none', boxSizing: 'border-box' }}
        />
      </div>

      {/* Due date — editable */}
      <div style={{ marginBottom: '16px' }}>
        <p style={{ fontSize: '11px', color: '#888', margin: '0 0 3px' }}>Due date</p>
        <input
          type="date"
          value={action.due_date || ''}
          onChange={e => update('due_date', e.target.value)}
          style={{ padding: '8px 10px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none' }}
        />
      </div>

      {/* Notes — editable */}
      <div style={{ marginBottom: '16px' }}>
        <p style={{ fontSize: '11px', color: '#888', margin: '0 0 3px' }}>Notes (optional)</p>
        <textarea
          value={action.notes || ''}
          onChange={e => update('notes', e.target.value)}
          rows={2}
          placeholder="Additional context..."
          style={{ width: '100%', padding: '8px 10px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none', resize: 'none', boxSizing: 'border-box', fontFamily: 'system-ui' }}
        />
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <button onClick={onConfirm} disabled={executing || (action.action_type === 'create_task' && !action.task_name)}
          style={{ padding: '8px 16px', fontSize: '12px', fontWeight: '500', background: executing ? '#eee' : '#27ae60', color: executing ? '#888' : '#fff', border: 'none', borderRadius: '6px', cursor: executing ? 'not-allowed' : 'pointer' }}>
          {executing ? 'Working...' : action.action_type === 'create_task' ? 'Confirm & Create' : 'Confirm & Update'}
        </button>
        <button onClick={onCancel} disabled={executing}
          style={{ padding: '8px 16px', fontSize: '12px', background: '#fff', color: '#888', border: '0.5px solid #ddd', borderRadius: '6px', cursor: 'pointer' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function Chat({ user }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [meActivity, setMeActivity] = useState([]);
  const [meLoaded, setMeLoaded] = useState(false);
  const bottomRef = React.useRef(null);

  React.useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  React.useEffect(() => {
    apiFetch('/chat/me-context')
      .then(r => r && r.json())
      .then(d => { setMeActivity(d.activity || []); setMeLoaded(true); })
      .catch(() => setMeLoaded(true));
  }, []);

  const [pendingAction, setPendingAction] = useState(null);
  const [executing, setExecuting] = useState(false);

  const handleSend = async (overrideMsg) => {
    const msg = (typeof overrideMsg === 'string' ? overrideMsg : input).trim();
    if (!msg || loading) return;
    const newMessages = [...messages, { role: 'user', content: msg }];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      // First check if this is an action request
      const actionRes = await apiFetch('/chat/action/parse', {
        method: 'POST',
        body: JSON.stringify({ message: msg })
      });
      const actionData = await actionRes.json();

      if (actionData['is_action'] === true && actionData.confidence !== 'low') {
        // Show confirmation card instead of chat response
        setPendingAction(actionData);
        setLoading(false);
        return;
      }

      // Otherwise answer as normal question
      const r = await apiFetch('/chat', {
        method: 'POST',
        body: JSON.stringify({
          message: msg,
          history: newMessages.slice(-6).map(m => ({ role: m.role, content: m.content })),
          me_activity: meActivity
        })
      });
      const d = await r.json();
      setMessages(prev => [...prev, { role: 'assistant', content: d.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Error — could not reach PRIMO.' }]);
    }
    setLoading(false);
  };

  const handleConfirmAction = async () => {
    if (!pendingAction) return;
    console.log('Sending intent:', JSON.stringify(pendingAction));
    setExecuting(true);
    try {
      const r = await apiFetch('/chat/action/execute', {
        method: 'POST',
        body: JSON.stringify({ intent: pendingAction })
      });
      const d = await r.json();
      const resultMsg = d.success
        ? `Done. ${d.message}${d.task_url ? ` — [View in Asana](${d.task_url})` : ''}`
        : `Could not complete action: ${d.message}`;
      setMessages(prev => [...prev, { role: 'assistant', content: resultMsg }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Error executing action.' }]);
    }
    setPendingAction(null);
    setExecuting(false);
  };

  const handleCancelAction = () => {
    setMessages(prev => [...prev, { role: 'assistant', content: 'Action cancelled.' }]);
    setPendingAction(null);
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <p style={{ fontSize: '12px', color: '#aaa', margin: 0 }}>Ask anything about your projects, decisions, or team portfolio. Answers are grounded in synced Asana data and uploaded transcripts.</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {meLoaded && <span style={{ fontSize: '11px', color: meActivity.length > 0 ? '#27ae60' : '#aaa' }}>
            {meActivity.length > 0 ? `ME: ${meActivity.length} tickets loaded` : 'ME: no recent activity'}
          </span>}
          {!meLoaded && <span style={{ fontSize: '11px', color: '#aaa' }}>Loading ME context...</span>}
          {messages.length > 0 && <button onClick={() => setMessages([])} style={{ fontSize: '11px', color: '#aaa', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px' }}>Clear</button>}
        </div>
      </div>

      <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {messages.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
            {[
              'Which of my projects are overdue?',
              'What needs attention this week?',
              'What is still open from my last meetings?',
              'Which projects are waiting on the client?'
            ].map((suggestion, i) => (
              <button key={i} onClick={() => handleSend(suggestion)}
                style={{ textAlign: 'left', padding: '10px 14px', fontSize: '13px', color: '#555', background: '#f8f9fa', border: '0.5px solid #e0e0e0', borderRadius: '8px', cursor: 'pointer' }}>
                {suggestion}
              </button>
            ))}
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{ maxWidth: '80%', padding: '10px 14px', borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px', background: msg.role === 'user' ? '#1a1a1a' : '#f0f4ff', color: msg.role === 'user' ? '#fff' : '#333', fontSize: '13px', lineHeight: '1.6', border: msg.role === 'assistant' ? '0.5px solid #c5cae9' : 'none' }}>
              {msg.role === 'assistant' ? formatText(msg.content) : msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div style={{ padding: '10px 14px', borderRadius: '12px 12px 12px 2px', background: '#f0f4ff', border: '0.5px solid #c5cae9', fontSize: '13px', color: '#888' }}>Thinking...</div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Action confirmation card */}
      {pendingAction && (
        <ActionConfirmCard
          action={pendingAction}
          executing={executing}
          onConfirm={handleConfirmAction}
          onCancel={handleCancelAction}
          onChange={setPendingAction}
        />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
          placeholder="Ask PRIMO anything about your projects..." rows={2}
          style={{ width: '100%', padding: '10px 14px', fontSize: '13px', border: '0.5px solid #ddd', borderRadius: '10px', outline: 'none', resize: 'none', fontFamily: 'system-ui', lineHeight: '1.5', boxSizing: 'border-box' }} />
        <button onClick={handleSend} disabled={loading || !input.trim()}
          style={{ width: '100%', padding: '10px', fontSize: '13px', fontWeight: '500', background: loading || !input.trim() ? '#eee' : '#cc0000', color: loading || !input.trim() ? '#aaa' : '#fff', border: 'none', borderRadius: '10px', cursor: loading || !input.trim() ? 'not-allowed' : 'pointer' }}>
          Send
        </button>
      </div>
      <p style={{ fontSize: '11px', color: '#ccc', margin: '6px 0 0', textAlign: 'center' }}>Enter to send · Shift+Enter for new line · Data syncs every 15 min</p>
    </div>
  );
}

function SegmentedDonut({ onTrack, atRisk, offTrack, onHold, total, size = 160 }) {
  const cx = size / 2;
  const cy = size / 2;
  const r = (size / 2) - 16;
  const circ = 2 * Math.PI * r;

  const segments = [
    { value: onTrack, color: '#27ae60', label: 'On Track' },
    { value: atRisk, color: '#e67e22', label: 'At Risk' },
    { value: offTrack, color: '#c0392b', label: 'Off Track' },
    { value: onHold, color: '#1a73e8', label: 'On Hold' },
  ];

  let offset = 0;
  const arcs = segments.map(seg => {
    const pct = total > 0 ? seg.value / total : 0;
    const dash = pct * circ;
    const arc = { ...seg, pct, dash, offset: -offset };
    offset += dash;
    return arc;
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '28px', flexWrap: 'wrap' }}>
      <svg width={size} height={size}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f0f0f0" strokeWidth="14" />
        {arcs.map((arc, i) => arc.value > 0 && (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={arc.color} strokeWidth="14"
            strokeDasharray={`${arc.dash} ${circ}`}
            strokeDashoffset={arc.offset}
            transform={`rotate(-90 ${cx} ${cy})`} />
        ))}
        <text x={cx} y={cy - 10} textAnchor="middle" style={{ fontSize: '24px', fontWeight: '700', fill: '#27ae60' }}>{total > 0 ? Math.round(onTrack / total * 100) : 0}%</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {segments.map((seg, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: seg.color, flexShrink: 0 }} />
            <span style={{ fontSize: '13px', color: '#444', minWidth: '70px' }}>{seg.label}</span>
            <span style={{ fontSize: '18px', fontWeight: '700', color: seg.color }}>{total > 0 ? Math.round(seg.value / total * 100) : 0}%</span>
            <span style={{ fontSize: '11px', color: '#aaa' }}>({seg.value})</span>
          </div>
        ))}
        <p style={{ fontSize: '11px', color: '#aaa', margin: '8px 0 0' }}>
          {total} active projects
        </p>
      </div>
    </div>
  );
}

function GaugeChart({ value, target, min = 0, max = 200, label, targetLabel, size = 200, reverse = false }) {
  // reverse=true means lower is better (consumption), false means higher is better (on track, signoff)
  const cx = size / 2;
  const cy = size / 2 + 10;
  const r = size / 2 - 16;
  const toRad = (deg) => (deg * Math.PI) / 180;

  // Half circle: 180deg from left to right (bottom half unused)
  const startAngle = 180; // left
  const endAngle = 0;     // right

  const valToAngle = (v) => {
    const pct = Math.min(Math.max((v - min) / (max - min), 0), 1);
    return 180 - pct * 180; // 180=left, 0=right
  };

  const polarToXY = (angle, radius) => ({
    x: cx + radius * Math.cos(toRad(angle)),
    y: cy - radius * Math.sin(toRad(angle))
  });

  const describeArc = (startDeg, endDeg, radius) => {
    const s = polarToXY(startDeg, radius);
    const e = polarToXY(endDeg, radius);
    const large = Math.abs(startDeg - endDeg) > 180 ? 1 : 0;
    // sweep=1 for clockwise in SVG coords
    return `M ${s.x} ${s.y} A ${radius} ${radius} 0 ${large} 1 ${e.x} ${e.y}`;
  };

  // Single color arc from min to current value
  const valueColor2 = reverse
    ? value <= 100 ? '#27ae60' : value <= 120 ? '#e67e22' : '#c0392b'
    : value >= target ? '#27ae60' : value >= target * 0.8 ? '#e67e22' : '#c0392b';

  // Needle
  const needleAngle = valToAngle(value || 0);
  const needleTip = polarToXY(needleAngle, r - 8);
  const needleBase1 = polarToXY(needleAngle + 90, 6);
  const needleBase2 = polarToXY(needleAngle - 90, 6);

  // Target marker
  const targetAngle = valToAngle(target);
  const tOuter = polarToXY(targetAngle, r + 4);
  const tInner = polarToXY(targetAngle, r - 14);

  // Value color
  const valueColor = reverse
    ? value <= 100 ? '#27ae60' : value <= 120 ? '#e67e22' : '#c0392b'
    : value >= target ? '#27ae60' : value >= target * 0.8 ? '#e67e22' : '#c0392b';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <svg width={size} height={size / 2 + 30} style={{ overflow: 'visible' }}>
        {/* Background track */}
        <path d={describeArc(180, 0, r)} fill="none" stroke="#ebebeb" strokeWidth="20" strokeLinecap="butt" />

        {/* Single value arc */}
        <path d={describeArc(180, valToAngle(value || 0), r)} fill="none" stroke={valueColor2} strokeWidth="20" strokeLinecap="butt" />

        {/* Target marker */}
        <line x1={tInner.x} y1={tInner.y} x2={tOuter.x} y2={tOuter.y} stroke="#333" strokeWidth="2" />

        {/* Needle */}
        <polygon points={`${needleTip.x},${needleTip.y} ${needleBase1.x},${needleBase1.y} ${needleBase2.x},${needleBase2.y}`}
          fill="#444" />
        <circle cx={cx} cy={cy} r="5" fill="#444" />

        {/* Value */}
        <text x={cx} y={cy - 10} textAnchor="middle" style={{ fontSize: '24px', fontWeight: '600', fill: '#666' }}>
          {value ? `${value}%` : 'N/A'}
        </text>

        {/* Min/Max labels */}
        <text x={polarToXY(180, r + 14).x} y={polarToXY(180, r + 14).y + 4} textAnchor="middle" style={{ fontSize: '10px', fill: '#aaa' }}>{min}%</text>
        <text x={polarToXY(0, r + 14).x} y={polarToXY(0, r + 14).y + 4} textAnchor="middle" style={{ fontSize: '10px', fill: '#aaa' }}>{max}%</text>

        {/* Target label */}
        <text x={tOuter.x} y={tOuter.y - 10} textAnchor="middle" style={{ fontSize: '10px', fill: '#333', fontWeight: '600' }}>{targetLabel}</text>
      </svg>
    </div>
  );
}

function EffortDonut({ summary, size = 160 }) {
  if (!summary || summary.total === 0) return null;
  const cx = size / 2;
  const cy = size / 2;
  const r = (size / 2) - 16;
  const circ = 2 * Math.PI * r;

  const within = summary.on_budget || 0;
  const over = summary.over_budget || 0;
  const under = summary.under_budget || 0;
  const total = within + over + under;

  const segments = [
    { value: within, color: '#27ae60', label: 'On target', sub: '80-120% consumed' },
    { value: over, color: '#c0392b', label: 'Over consumed', sub: '>120% consumed' },
    { value: under, color: '#1a73e8', label: 'Under consumed', sub: '<80% consumed' },
  ];

  let offset = 0;
  const arcs = segments.map(seg => {
    const pct = total > 0 ? seg.value / total : 0;
    const dash = pct * circ;
    const arc = { ...seg, pct, dash, offset: -offset };
    offset += dash;
    return arc;
  });

  const accuracyColor = (pct) => {
    if (!pct) return '#aaa';
    if (pct > 120) return '#c0392b';
    if (pct > 100) return '#e67e22';
    if (pct >= 80) return '#27ae60';
    return '#1a73e8';
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '28px', flexWrap: 'wrap' }}>
      <svg width={size} height={size}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f0f0f0" strokeWidth="14" />
        {arcs.map((arc, i) => arc.value > 0 && (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={arc.color} strokeWidth="14"
            strokeDasharray={`${arc.dash} ${circ}`}
            strokeDashoffset={arc.offset}
            transform={`rotate(-90 ${cx} ${cy})`} />
        ))}
        <text x={cx} y={cy - 10} textAnchor="middle" style={{ fontSize: '24px', fontWeight: '700', fill: accuracyColor(summary.mean_accuracy) }}>
          {summary.mean_accuracy ? `${summary.mean_accuracy}%` : 'N/A'}
        </text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {segments.map((seg, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: seg.color, flexShrink: 0 }} />
            <span style={{ fontSize: '13px', color: '#444', minWidth: '110px' }}>{seg.label}</span>
            <span style={{ fontSize: '18px', fontWeight: '700', color: seg.color }}>
              {total > 0 ? Math.round(seg.value / total * 100) : 0}%
            </span>
            <span style={{ fontSize: '11px', color: '#aaa' }}>({seg.value})</span>
          </div>
        ))}
        {summary.avg_estimated && (
          <p style={{ fontSize: '11px', color: '#aaa', margin: '4px 0 0' }}>
            Avg {summary.avg_estimated}h est. · {summary.avg_consumed}h consumed
          </p>
        )}
      </div>
    </div>
  );
}

function SignoffDonut({ tacit, size = 160 }) {
  if (!tacit || tacit.total === 0) return null;
  const cx = size / 2;
  const cy = size / 2;
  const r = (size / 2) - 16;
  const circ = 2 * Math.PI * r;

  const received = tacit.received || 0;
  const tacitCount = tacit.tacit || 0;
  const total = received + tacitCount;

  const segments = [
    { value: received, color: '#27ae60', label: 'Signed off' },
    { value: tacitCount, color: '#e67e22', label: 'Tacit closure' },
  ];

  let offset = 0;
  const arcs = segments.map(seg => {
    const pct = total > 0 ? seg.value / total : 0;
    const dash = pct * circ;
    const arc = { ...seg, pct, dash, offset: -offset };
    offset += dash;
    return arc;
  });

  const signoffRate = total > 0 ? Math.round(received / total * 100) : 0;
  const rateColor = signoffRate >= 70 ? '#27ae60' : signoffRate >= 50 ? '#e67e22' : '#c0392b';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '28px', flexWrap: 'wrap' }}>
      <svg width={size} height={size}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f0f0f0" strokeWidth="14" />
        {arcs.map((arc, i) => arc.value > 0 && (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={arc.color} strokeWidth="14"
            strokeDasharray={`${arc.dash} ${circ}`}
            strokeDashoffset={arc.offset}
            transform={`rotate(-90 ${cx} ${cy})`} />
        ))}
        <text x={cx} y={cy - 10} textAnchor="middle" style={{ fontSize: '24px', fontWeight: '700', fill: rateColor }}>{signoffRate}%</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {segments.map((seg, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: seg.color, flexShrink: 0 }} />
            <span style={{ fontSize: '13px', color: '#444', minWidth: '90px' }}>{seg.label}</span>
            <span style={{ fontSize: '18px', fontWeight: '700', color: seg.color }}>{total > 0 ? Math.round(seg.value / total * 100) : 0}%</span>
            <span style={{ fontSize: '11px', color: '#aaa' }}>({seg.value})</span>
          </div>
        ))}
        <div style={{ marginTop: '8px', padding: '10px', background: '#f8f9fa', borderRadius: '8px' }}>
          <p style={{ fontSize: '11px', color: '#aaa', margin: 0, fontStyle: 'italic' }}>Delivery metrics coming soon</p>
        </div>
      </div>
    </div>
  );
}

function Metrics({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pmFilter, setPmFilter] = useState('all');
  const [allData, setAllData] = useState(null);
  const [pmExpanded, setPmExpanded] = useState(false);
  const [effortData, setEffortData] = useState(null);
  const [effortLoading, setEffortLoading] = useState(true);
  const [effortYear, setEffortYear] = useState(null);
  const [effortMonth, setEffortMonth] = useState(null);
  const [effortPm, setEffortPm] = useState(null);
  const [effortTech, setEffortTech] = useState(null);
  const [showProjects, setShowProjects] = useState(false);
  const [showTrend, setShowTrend] = useState(true);
  const [showPipeline, setShowPipeline] = useState(false);
  const [showEffortFilters, setShowEffortFilters] = useState(false);

  useEffect(() => {
    apiFetch('/metrics')
      .then(r => r && r.json())
      .then(d => { setData(d); setAllData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (effortYear) params.set('year', effortYear);
    if (effortMonth) params.set('month', effortMonth);
    if (effortPm) params.set('owner_name', effortPm);
    if (effortTech) params.set('technology', effortTech);
    setEffortLoading(true);
    apiFetch(`/metrics/effort?${params.toString()}`)
      .then(r => r && r.json())
      .then(d => { setEffortData(d); setEffortLoading(false); })
      .catch(() => setEffortLoading(false));
  }, [effortYear, effortMonth, effortPm, effortTech]);

  const handlePmFilter = async (pm) => {
    setPmFilter(pm);
    if (pm === 'all') {
      setData(allData);
      return;
    }
    setLoading(true);
    try {
      const r = await apiFetch(`/metrics?pm=${encodeURIComponent(pm)}`);
      const d = await r.json();
      setData({ ...d, pm_breakdown: allData.pm_breakdown });
    } catch {}
    setLoading(false);
  };

  const accuracyColor = (pct) => {
    if (!pct) return '#aaa';
    if (pct > 120) return '#c0392b';
    if (pct > 100) return '#e67e22';
    if (pct >= 80) return '#27ae60';
    return '#1a73e8';
  };

  const monthName = (m) => ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m-1];

  if (loading) return <p style={{ color: '#aaa', fontSize: '13px' }}>Loading metrics...</p>;
  if (!data) return <p style={{ color: '#aaa', fontSize: '13px' }}>No data available.</p>;

  const isLead = user.role === 'lead';
  const health = data.portfolio_health;
  const pms = data.pm_breakdown || [];

  const StatCard = ({ label, value, sub, color }) => (
    <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: '16px 20px', flex: 1, minWidth: '120px' }}>
      <p style={{ fontSize: '11px', color: '#888', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</p>
      <p style={{ fontSize: '28px', fontWeight: '600', margin: '0 0 2px', color: color || '#1a1a1a' }}>{value}</p>
      {sub && <p style={{ fontSize: '11px', color: '#aaa', margin: 0 }}>{sub}</p>}
    </div>
  );

  const signoff = data.signoff_pipeline || [];
  const pending = data.pending_signoff || [];
  const tacit = data.tacit_closure || {};

  const filteredSignoff = pmFilter === 'all' ? signoff : signoff.filter(p => p.pm === pmFilter);
  const filteredPending = pmFilter === 'all' ? pending : pending.filter(p => p.pm === pmFilter);

  const statusColor = {
    'Enviar': '#e67e22',
    'Enviado': '#1a73e8',
    'Recebido': '#27ae60',
    'Fechado Tacitamente': '#888'
  };

  return (
    <div>
      {/* Portfolio health — donut charts */}
      <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: '16px 20px', marginBottom: '16px' }}>
        <p style={{ fontSize: '11px', fontWeight: '600', color: '#444', margin: '0 0 12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Consumption Ratio</p>

        {/* Filters — collapsible */}
        <div style={{ marginTop: '16px', borderTop: '1px solid #f0f0f0', paddingTop: '12px', marginBottom: '16px' }}>
          <div onClick={() => setShowEffortFilters(!showEffortFilters)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: showEffortFilters ? '12px' : 0 }}>
            <p style={{ fontSize: '11px', fontWeight: '600', color: '#444', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Filters{(effortYear || effortMonth || effortPm || effortTech) ? ' · active' : ''}
            </p>
            <span style={{ fontSize: '12px', color: '#aaa' }}>{showEffortFilters ? '▲' : '▼'}</span>
          </div>
        {showEffortFilters && <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {/* Year */}
          {effortData?.available_years?.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <p style={{ fontSize: '11px', color: '#888', margin: 0, minWidth: '40px' }}>Year</p>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                <button onClick={() => { setEffortYear(null); setEffortMonth(null); }}
                  style={{ padding: '3px 10px', fontSize: '11px', borderRadius: '12px', border: '0.5px solid #ddd', background: !effortYear ? '#1a1a1a' : '#fff', color: !effortYear ? '#fff' : '#666', cursor: 'pointer' }}>
                  All
                </button>
                {effortData.available_years.map(y => (
                  <button key={y} onClick={() => { setEffortYear(effortYear === y ? null : y); setEffortMonth(null); }}
                    style={{ padding: '3px 10px', fontSize: '11px', borderRadius: '12px', border: '0.5px solid #ddd', background: effortYear === y ? '#1a1a1a' : '#fff', color: effortYear === y ? '#fff' : '#666', cursor: 'pointer' }}>
                    {y}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Month — only when year selected */}
          {effortYear && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <p style={{ fontSize: '11px', color: '#888', margin: 0, minWidth: '40px' }}>Month</p>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                <button onClick={() => setEffortMonth(null)}
                  style={{ padding: '3px 10px', fontSize: '11px', borderRadius: '12px', border: '0.5px solid #ddd', background: !effortMonth ? '#1a1a1a' : '#fff', color: !effortMonth ? '#fff' : '#666', cursor: 'pointer' }}>
                  All
                </button>
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                  <button key={m} onClick={() => setEffortMonth(effortMonth === m ? null : m)}
                    style={{ padding: '3px 10px', fontSize: '11px', borderRadius: '12px', border: '0.5px solid #ddd', background: effortMonth === m ? '#1a1a1a' : '#fff', color: effortMonth === m ? '#fff' : '#666', cursor: 'pointer' }}>
                    {monthName(m)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* PM — lead only */}
          {user.role === 'lead' && effortData?.by_pm?.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <p style={{ fontSize: '11px', color: '#888', margin: 0, minWidth: '40px' }}>PM</p>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                <button onClick={() => setEffortPm(null)}
                  style={{ padding: '3px 10px', fontSize: '11px', borderRadius: '12px', border: '0.5px solid #ddd', background: !effortPm ? '#1a1a1a' : '#fff', color: !effortPm ? '#fff' : '#666', cursor: 'pointer' }}>
                  All
                </button>
                {effortData.by_pm.map(pm => (
                  <button key={pm.owner_name} onClick={() => setEffortPm(effortPm === pm.owner_name ? null : pm.owner_name)}
                    style={{ padding: '3px 10px', fontSize: '11px', borderRadius: '12px', border: '0.5px solid #ddd', background: effortPm === pm.owner_name ? '#1a1a1a' : '#fff', color: effortPm === pm.owner_name ? '#fff' : '#666', cursor: 'pointer' }}>
                    {pm.owner_name.split(' ')[0]}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Technology */}
          {effortData?.technologies?.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <p style={{ fontSize: '11px', color: '#888', margin: 0, minWidth: '40px' }}>Tech</p>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                <button onClick={() => setEffortTech(null)}
                  style={{ padding: '3px 10px', fontSize: '11px', borderRadius: '12px', border: '0.5px solid #ddd', background: !effortTech ? '#1a1a1a' : '#fff', color: !effortTech ? '#fff' : '#666', cursor: 'pointer' }}>
                  All
                </button>
                {effortData.technologies.map(t => (
                  <button key={t} onClick={() => setEffortTech(effortTech === t ? null : t)}
                    style={{ padding: '3px 10px', fontSize: '11px', borderRadius: '12px', border: '0.5px solid #ddd', background: effortTech === t ? '#1a1a1a' : '#fff', color: effortTech === t ? '#fff' : '#666', cursor: 'pointer' }}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>}
        </div>

        {effortLoading && <p style={{ fontSize: '12px', color: '#aaa', margin: 0 }}>Loading...</p>}

        {effortData && !effortLoading && (
          <>
            {/* Effort gauge + donut */}
            <div style={{ display: 'flex', gap: '0', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
              <div style={{ paddingRight: '24px', borderRight: '1px solid #f0f0f0' }}>
                <GaugeChart
                  value={effortData.summary.mean_accuracy}
                  target={100}
                  min={0}
                  max={200}
                  label="avg. consumption rate"
                  targetLabel="≤100%"
                  reverse={true}
                  size={200}
                />
              </div>
              <div style={{ paddingLeft: '24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[
                  { label: 'On target', value: effortData.summary.on_budget, color: '#27ae60' },
                  { label: 'Over consumed', value: effortData.summary.over_budget, color: '#c0392b' },
                  { label: 'Under consumed', value: effortData.summary.under_budget, color: '#1a73e8' },
                ].map((seg, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: seg.color, flexShrink: 0 }} />
                    <span style={{ fontSize: '13px', color: '#444', minWidth: '110px' }}>{seg.label}</span>
                    <span style={{ fontSize: '18px', fontWeight: '700', color: seg.color }}>{effortData.summary.total > 0 ? Math.round(seg.value / effortData.summary.total * 100) : 0}%</span>
                    <span style={{ fontSize: '11px', color: '#aaa' }}>({seg.value})</span>
                  </div>
                ))}
                <p style={{ fontSize: '11px', color: '#aaa', margin: '4px 0 0' }}>
                  Avg {effortData.summary.avg_estimated}h est. · {effortData.summary.avg_consumed}h consumed
                </p>
              </div>
            </div>
            <p style={{ fontSize: '12px', color: '#666', margin: '0 0 16px' }}>
              Total estimated: <strong>{effortData.summary.total_estimated}h</strong> · Total consumed: <strong>{effortData.summary.total_consumed}h</strong>
            </p>





            {/* Monthly trend — collapsed */}
            {effortData.by_month.length > 1 && (
              <div style={{ marginBottom: '12px', borderTop: '1px solid #f0f0f0', paddingTop: '12px' }}>
                <div onClick={() => setShowTrend(!showTrend)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: showTrend ? '12px' : 0 }}>
                  <p style={{ fontSize: '11px', fontWeight: '600', color: '#444', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Monthly trend</p>
                  <span style={{ fontSize: '12px', color: '#aaa' }}>{showTrend ? '▲' : '▼'}</span>
                </div>
                {showTrend && (
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'flex-end', height: '60px', marginTop: '8px' }}>
                    {effortData.by_month.map((m, i) => {
                      const h = Math.min(Math.max(m.mean_accuracy / 150 * 60, 4), 60);
                      return (
                        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                          <p style={{ fontSize: '10px', color: '#aaa', margin: 0 }}>{Math.round(m.mean_accuracy)}%</p>
                          <div style={{ width: '100%', height: `${h}px`, background: accuracyColor(m.mean_accuracy), borderRadius: '2px', opacity: 0.25 }} />
                          <p style={{ fontSize: '10px', color: '#666', margin: 0 }}>{monthName(m.month)}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Project list toggle */}
            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '12px' }}>
            <div onClick={() => setShowProjects(!showProjects)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: showProjects ? '12px' : 0 }}>
              <p style={{ fontSize: '11px', fontWeight: '600', color: '#444', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {showProjects ? 'Projects' : `Projects (${effortData.projects.length})`}
              </p>
              <span style={{ fontSize: '12px', color: '#aaa' }}>{showProjects ? '▲' : '▼'}</span>
            </div>

            </div>
            {showProjects && (
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {effortData.projects.map((p, i) => {
                  const ticketMatch = p.name.match(/:: (\d+)/);
                  const ticketId = ticketMatch ? ticketMatch[1] : null;
                  const clientPart = p.name.split(' :: ')[0];
                  const descPart = p.name.split(' - ').slice(1).join(' - ');
                  const incidentPct = p.consumed > 0 && p.incident_hours > 0 ? Math.round(p.incident_hours / p.consumed * 100) : null;
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0', gap: '10px' }}>
                      {ticketId && (
                        <p style={{ fontSize: '11px', fontWeight: '600', color: '#888', margin: 0, minWidth: '52px', fontFamily: 'monospace' }}>#{ticketId}</p>
                      )}
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: '12px', margin: '0 0 1px', fontWeight: '500' }}>{clientPart}{descPart ? ` — ${descPart}` : ''}</p>
                        <p style={{ fontSize: '11px', color: '#aaa', margin: 0 }}>{p.pm?.split(' ')[0]} · {p.closed_at} · {p.estimated}h est.</p>
                      </div>
                      {incidentPct !== null && (
                        <p style={{ fontSize: '12px', fontWeight: '600', color: '#c0392b', margin: 0, minWidth: '48px', textAlign: 'right' }}>{incidentPct}% inc.</p>
                      )}
                      <p style={{ fontSize: '13px', fontWeight: '700', color: accuracyColor(p.accuracy_pct), margin: 0, minWidth: '48px', textAlign: 'right' }}>{p.accuracy_pct}%</p>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

      </div>
      <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: '16px 20px', marginBottom: '16px' }}>
        <p style={{ fontSize: '11px', fontWeight: '600', color: '#444', margin: '0 0 16px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          On Track Rate
        </p>
        <div style={{ display: 'flex', gap: '0', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ paddingRight: '24px', borderRight: '1px solid #f0f0f0' }}>
            <GaugeChart
              value={health.on_track + health.at_risk + health.off_track > 0 ? Math.round(health.on_track / (health.on_track + health.at_risk + health.off_track) * 100) : 0}
              target={75}
              min={0}
              max={100}
              label="on track rate"
              targetLabel="≥75%"
              reverse={false}
              size={200}
            />
          </div>
          <div style={{ paddingLeft: '24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[
              { label: 'On Track', value: health.on_track, color: '#27ae60' },
              { label: 'At Risk', value: health.at_risk, color: '#e67e22' },
              { label: 'Off Track', value: health.off_track, color: '#c0392b' },
              { label: 'On Hold', value: health.on_hold, color: '#1a73e8' },
            ].map((seg, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: seg.color, flexShrink: 0 }} />
                <span style={{ fontSize: '13px', color: '#444', minWidth: '70px' }}>{seg.label}</span>
                <span style={{ fontSize: '18px', fontWeight: '700', color: seg.color }}>{health.total > 0 ? Math.round(seg.value / health.total * 100) : 0}%</span>
                <span style={{ fontSize: '11px', color: '#aaa' }}>({seg.value})</span>
              </div>
            ))}
            <p style={{ fontSize: '11px', color: '#aaa', margin: '4px 0 0' }}>{health.total} active projects</p>
          </div>
        </div>
        {/* PM Breakdown */}
        {isLead && pms.length > 0 && (
          <div style={{ marginTop: '16px', borderTop: '1px solid #f0f0f0', paddingTop: '12px' }}>
            <div onClick={() => setPmExpanded(!pmExpanded)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: pmExpanded ? '12px' : 0 }}>
              <p style={{ fontSize: '11px', fontWeight: '600', color: '#444', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>By Project Manager</p>
              <span style={{ fontSize: '12px', color: '#aaa' }}>{pmExpanded ? '\u25b2' : '\u25bc'}</span>
            </div>
            {pmExpanded && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginTop: '8px' }}>
              {pms.map(pm => (
                <div key={pm.pm} style={{ background: '#f8f9fa', borderRadius: '10px', padding: '14px 16px' }}>
                  <p style={{ fontSize: '13px', fontWeight: '600', margin: '0 0 10px', color: '#1a1a1a' }}>{pm.pm.split(' ')[0]} {pm.pm.split(' ')[1]}</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                      <span style={{ color: '#888' }}>Total</span>
                      <span style={{ fontWeight: '500' }}>{pm.total}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                      <span style={{ color: '#27ae60' }}>On track</span>
                      <span style={{ fontWeight: '500', color: '#27ae60' }}>{pm.on_track}</span>
                    </div>
                    {pm.at_risk > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                      <span style={{ color: '#e67e22' }}>At Risk</span>
                      <span style={{ fontWeight: '500', color: '#e67e22' }}>{pm.at_risk}</span>
                    </div>}
                    {pm.off_track > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                      <span style={{ color: '#c0392b' }}>Off Track</span>
                      <span style={{ fontWeight: '500', color: '#c0392b' }}>{pm.off_track}</span>
                    </div>}
                    {pm.on_hold > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                      <span style={{ color: '#1a73e8' }}>On Hold</span>
                      <span style={{ fontWeight: '500', color: '#1a73e8' }}>{pm.on_hold}</span>
                    </div>}
                    {pm.pending_signoff > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                      <span style={{ color: '#888' }}>Sign-off</span>
                      <span style={{ fontWeight: '500', color: '#888' }}>{pm.pending_signoff}</span>
                    </div>}
                  </div>
                </div>
              ))}
            </div>}
          </div>
        )}
      </div>
      {/* Incident Rate KPI */}
      {effortData && effortData.summary.total > 0 && (
        <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: '16px 20px', marginBottom: '16px' }}>
          <p style={{ fontSize: '11px', fontWeight: '600', color: '#444', margin: '0 0 16px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Incident Rate</p>
          <div style={{ display: 'flex', gap: '0', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ paddingRight: '24px', borderRight: '1px solid #f0f0f0' }}>
              <GaugeChart
                value={effortData.summary.incident_rate}
                target={10}
                min={0}
                max={50}
                label="incident rate"
                targetLabel="≤10%"
                reverse={true}
                size={200}
              />
            </div>
            <div style={{ paddingLeft: '24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '13px', color: '#444', minWidth: '160px' }}>Total incident hours</span>
                <span style={{ fontSize: '18px', fontWeight: '700', color: '#c0392b' }}>{effortData.summary.total_incident_hours}h</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '13px', color: '#444', minWidth: '160px' }}>Projects with incidents</span>
                <span style={{ fontSize: '18px', fontWeight: '700', color: '#e67e22' }}>{effortData.summary.projects_with_incidents}</span>
                <span style={{ fontSize: '11px', color: '#aaa' }}>of {effortData.summary.total}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '13px', color: '#444', minWidth: '160px' }}>Total consumed hours</span>
                <span style={{ fontSize: '18px', fontWeight: '700', color: '#888' }}>{effortData.summary.total_consumed}h</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sign-off Rate KPI */}
      {tacit.total > 0 && (
        <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: '16px 20px', marginBottom: '16px' }}>
          <p style={{ fontSize: '11px', fontWeight: '600', color: '#444', margin: '0 0 16px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Sign-off Rate</p>
          <div style={{ display: 'flex', gap: '0', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ paddingRight: '24px', borderRight: '1px solid #f0f0f0' }}>
              <GaugeChart
                value={tacit.total > 0 ? Math.round(tacit.received / tacit.total * 100) : 0}
                target={90}
                min={0}
                max={100}
                label="sign-off rate"
                targetLabel="≥90%"
                reverse={false}
                size={200}
              />
            </div>
            <div style={{ paddingLeft: '24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { label: 'Signed off', value: tacit.received, color: '#27ae60' },
                { label: 'Tacit closure', value: tacit.tacit, color: '#e67e22' },
              ].map((seg, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: seg.color, flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', color: '#444', minWidth: '100px' }}>{seg.label}</span>
                  <span style={{ fontSize: '18px', fontWeight: '700', color: seg.color }}>{tacit.total > 0 ? Math.round(seg.value / tacit.total * 100) : 0}%</span>
                  <span style={{ fontSize: '11px', color: '#aaa' }}>({seg.value})</span>
                </div>
              ))}
              <p style={{ fontSize: '11px', color: '#aaa', margin: '4px 0 0' }}>{tacit.total} closed projects</p>
            </div>
          </div>
          {filteredSignoff.length > 0 && (
            <div style={{ marginTop: '16px', borderTop: '1px solid #f0f0f0', paddingTop: '12px' }}>
              <div onClick={() => setShowPipeline(!showPipeline)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: showPipeline ? '12px' : 0 }}>
                <p style={{ fontSize: '11px', fontWeight: '600', color: '#444', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pipeline ({filteredSignoff.length})</p>
                <span style={{ fontSize: '12px', color: '#aaa' }}>{showPipeline ? '\u25b2' : '\u25bc'}</span>
              </div>
              {showPipeline && filteredSignoff.map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < filteredSignoff.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '13px', margin: '0 0 2px', fontWeight: '500' }}>{p.name}</p>
                    <p style={{ fontSize: '11px', color: '#aaa', margin: 0 }}>{p.pm}{p.due_date ? ` · due ${p.due_date}` : ''}</p>
                  </div>
                  <span style={{ fontSize: '11px', fontWeight: '500', padding: '3px 10px', borderRadius: '12px', background: (statusColor[p.status] || '#888') + '18', color: statusColor[p.status] || '#888' }}>
                    {p.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <p style={{ fontSize: '11px', color: '#ccc', margin: '16px 0 0', textAlign: 'center' }}>Data from Asana sync · updated every 15 min</p>
    </div>
  );
}


function App() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState('daywatch');
  const [syncing, setSyncing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [lastAction, setLastAction] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    const token = getToken();
    if (!token) { setChecking(false); return; }
    apiFetch('/auth/me').then(r => r && r.json()).then(d => {
      if (d && d.gid) setUser(d); else clearToken();
      setChecking(false);
    }).catch(() => { clearToken(); setChecking(false); });
  }, []);

  useEffect(() => {
    if (!user) return;
    apiFetch('/sync').then(r => r && r.json()).then(d => { if (d && d.synced) setProjects(d.synced); }).catch(() => {});
  }, [user]);

  const handleLogout = () => { clearToken(); setUser(null); };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await apiFetch('/sync'); const d = await r.json();
      if (d.synced) setProjects(d.synced);
      setLastAction(`Synced ${d.total_projects} projects`);
    } catch { setLastAction('Sync failed'); }
    setSyncing(false);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try { await apiFetch('/plans/generate'); setLastAction('Plans generated — refresh to see updates'); }
    catch { setLastAction('Generation failed'); }
    setGenerating(false);
  };

  const [teamSubTab, setTeamSubTab] = useState('overview');
  if (checking) return (
    <div style={{ fontFamily: 'system-ui', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#f5f6f8' }}>
      <p style={{ color: '#aaa', fontSize: '13px' }}>Loading...</p>
    </div>
  );

  if (!user) return <LoginScreen onLogin={setUser} />;

  const isLead = user.role === 'lead';
  const myMember = TEAM.find(m => m.gid === user.gid);

  const tabs = [
    { id: 'daywatch', label: "Today's Watch" },
    { id: 'my-plan', label: 'Week Plan' },
    { id: 'transcripts', label: 'Projects' },
    { id: 'chat', label: 'Chat' },
    ...(isLead ? [
    { id: 'team', label: 'Team' },
    { id: 'metrics', label: 'Metrics' },
    ] : [])
  ];

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif', maxWidth: '860px', margin: '0 auto', padding: '32px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <img src="/ricoh-logo.png" alt="Ricoh" style={{ height: '32px' }} />
          <div>
            <h1 style={{ fontSize: '20px', fontWeight: '600', margin: '0 0 1px' }}>PRIMO</h1>
            <p style={{ color: '#888', margin: 0, fontSize: '11px' }}>Project Intelligence &amp; Management Operator</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>{user.name}</p>
          <button onClick={handleLogout} style={{ padding: '6px 12px', fontSize: '12px', border: '0.5px solid #ddd', borderRadius: '8px', background: '#fff', cursor: 'pointer', color: '#888' }}>Sign out</button>
        </div>
      </div>

      {isLead && (
        <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
          <button onClick={handleSync} disabled={syncing} style={{ padding: '7px 14px', fontSize: '12px', border: '0.5px solid #ddd', borderRadius: '8px', background: '#fff', cursor: 'pointer' }}>
            {syncing ? 'Syncing...' : 'Sync Asana'}
          </button>
          <button onClick={handleGenerate} disabled={generating} style={{ padding: '7px 14px', fontSize: '12px', border: '0.5px solid #ddd', borderRadius: '8px', background: '#fff', cursor: 'pointer' }}>
            {generating ? 'Generating...' : 'Generate plans'}
          </button>
        </div>
      )}

      {lastAction && <p style={{ fontSize: '11px', color: '#aaa', margin: '4px 0 0' }}>{lastAction}</p>}

      <div style={{ display: 'flex', borderBottom: '1px solid #eee', margin: '24px 0 20px' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '10px 18px', fontSize: '13px', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: tab === t.id ? '500' : '400',
            color: tab === t.id ? '#1a1a1a' : '#888',
            borderBottom: tab === t.id ? '2px solid #cc0000' : '2px solid transparent',
            marginBottom: '-1px'
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'daywatch' && myMember && <DayWatch member={myMember} />}
      {tab === 'my-plan' && myMember && <PlanCard member={myMember} />}
      {tab === 'transcripts' && <Projects user={user} projects={projects} />}
      {tab === 'chat' && <Chat user={user} />}
      {tab === 'metrics' && isLead && <Metrics user={user} />}
      {tab === 'team' && isLead && (
        <div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <button onClick={() => setTeamSubTab('overview')}
              style={{ padding: '6px 14px', fontSize: '12px', fontWeight: teamSubTab === 'overview' ? '600' : '400', background: teamSubTab === 'overview' ? '#1a1a1a' : '#fff', color: teamSubTab === 'overview' ? '#fff' : '#888', border: '0.5px solid #ddd', borderRadius: '20px', cursor: 'pointer' }}>
              Overview
            </button>
            <button onClick={() => setTeamSubTab('pulse')}
              style={{ padding: '6px 14px', fontSize: '12px', fontWeight: teamSubTab === 'pulse' ? '600' : '400', background: teamSubTab === 'pulse' ? '#1a1a1a' : '#fff', color: teamSubTab === 'pulse' ? '#fff' : '#888', border: '0.5px solid #ddd', borderRadius: '20px', cursor: 'pointer' }}>
              Pulse
            </button>
          </div>
          {teamSubTab === 'overview' && <TeamOverview />}
          {teamSubTab === 'pulse' && <TeamPulse />}
        </div>
      )}
    </div>
  );
}

export default App;
